import {
  AccountStatus,
  ApproverFallbackStrategy,
  type Permission,
  rolesHavePermission,
  type UserRole
} from '@insio/shared-types';
import type { LifecycleDocument, LifecycleStepRef } from '../types';

/** The chain as edited; an empty id is "nobody picked". */
export interface FallbackDraft {
  strategy: ApproverFallbackStrategy;
  userId: string;
  orgUnitId: string;
  role: string;
  /** Whether the user who submitted the document is barred from approving it. */
  isSelfApprovalForbidden: boolean;
  /** Whether the document's creator names an approver the chain did not find. */
  isManualApproverSelectionAllowed: boolean;
}

/** The chain as the server returns it. */
export interface StoredFallback {
  strategy: ApproverFallbackStrategy;
  user?: { id: string } | null;
  orgUnit?: { id: string } | null;
  role?: string | null;
  isSelfApprovalForbidden?: boolean | null;
  isManualApproverSelectionAllowed?: boolean | null;
}

/** A member who may approve this kind, as the chain sees them: their roles and where they sit. */
export interface FallbackUser {
  readonly id: string;
  readonly displayName: string;
  readonly status: string;
  readonly roles: readonly string[];
  readonly orgUnits: readonly { readonly id: string }[];
}

/** What a chain is checked against before it is stored. */
export interface FallbackContext {
  /** Everyone who may approve this kind (`approverCandidates`); undefined until they are known. */
  readonly users: readonly FallbackUser[] | undefined;
  /** Roles granting the approve permission — the only ones the role chain may name. */
  readonly eligibleRoles: readonly UserRole[];
  readonly approvePermission: Permission;
}

/** Why a chain cannot be stored as picked. */
export type FallbackIssue =
  | { readonly kind: 'USER_CANNOT_APPROVE' }
  | { readonly kind: 'UNIT_HAS_NO_APPROVER' }
  | { readonly kind: 'ROLE_HAS_NO_APPROVER' };

/** The chain whose first step is the picked user — step 2 then names them. */
export const requiresUser = (strategy: ApproverFallbackStrategy) =>
  strategy === ApproverFallbackStrategy.FALLBACK_USER_THEN_ANYONE_WITH_PERMISSION;
export const usesOrgUnit = (strategy: ApproverFallbackStrategy) =>
  strategy === ApproverFallbackStrategy.ORG_UNIT_MANAGER_THEN_ANYONE_WITH_PERMISSION ||
  strategy === ApproverFallbackStrategy.ORG_UNIT_MEMBER_THEN_ANYONE_WITH_PERMISSION;
/** Chains that resolve the approver through the org structure. */
export const usesOrgStructure = (strategy: ApproverFallbackStrategy) =>
  usesOrgUnit(strategy) ||
  strategy ===
    ApproverFallbackStrategy.ORG_STRUCTURE_THEN_FALLBACK_USER_THEN_ANYONE_WITH_PERMISSION ||
  strategy === ApproverFallbackStrategy.DOCUMENT_UNIT_MANAGER_THEN_ANYONE_WITH_PERMISSION;
/** Active members with no unit, so no manager to reach; only a chain reading the org structure
 * misses them, so none for any other. */
export function usersOutsideOrgStructure<TUser extends FallbackUser>(
  users: readonly TUser[],
  strategy: ApproverFallbackStrategy
): TUser[] {
  if (!usesOrgStructure(strategy)) return [];
  return users.filter(user => user.status === AccountStatus.ACTIVE && user.orgUnits.length === 0);
}
export const usesRole = (strategy: ApproverFallbackStrategy) =>
  strategy === ApproverFallbackStrategy.ROLE_MEMBER_THEN_ANYONE_WITH_PERMISSION;
/** Manual selection runs no step at all, so there is no step 3 after it. */
export const hasRecipientStep = (strategy: ApproverFallbackStrategy) =>
  strategy !== ApproverFallbackStrategy.MANUAL_APPROVER_SELECTION;

const mayApprove = (user: FallbackUser, permission: Permission) =>
  user.status === AccountStatus.ACTIVE &&
  rolesHavePermission([...user.roles] as UserRole[], permission);

/**
 * Why the picks would skip the step: a user who is no candidate, or a unit or role nobody in may
 * approve. Nothing is flagged until the candidates are known; the form locks the chain till then.
 */
export function fallbackIssueOf(draft: FallbackDraft, ctx: FallbackContext): FallbackIssue | null {
  const { users } = ctx;
  if (users === undefined) return null;
  if (draft.userId !== '') {
    const user = users.find(candidate => candidate.id === draft.userId);
    if (!user || !mayApprove(user, ctx.approvePermission)) {
      return { kind: 'USER_CANNOT_APPROVE' };
    }
  }
  if (usesOrgUnit(draft.strategy) && draft.orgUnitId !== '') {
    const hasApprover = users.some(
      user =>
        user.orgUnits.some(unit => unit.id === draft.orgUnitId) &&
        mayApprove(user, ctx.approvePermission)
    );
    if (!hasApprover) return { kind: 'UNIT_HAS_NO_APPROVER' };
  }
  if (usesRole(draft.strategy) && draft.role !== '') {
    const role = draft.role as UserRole;
    const hasHolder = users.some(
      user => user.status === AccountStatus.ACTIVE && user.roles.includes(role)
    );
    if (!ctx.eligibleRoles.includes(role) || !hasHolder) return { kind: 'ROLE_HAS_NO_APPROVER' };
  }
  return null;
}

/** The one pick the chosen chain still needs, or null when it has everything. */
export function missingPickOf(draft: FallbackDraft): 'USER' | 'ORG_UNIT' | 'ROLE' | null {
  if (requiresUser(draft.strategy) && draft.userId === '') return 'USER';
  if (usesOrgUnit(draft.strategy) && draft.orgUnitId === '') return 'ORG_UNIT';
  if (usesRole(draft.strategy) && draft.role === '') return 'ROLE';
  return null;
}

/** Whether the chain may be stored: it has what it needs, and what it names may approve. */
export const isFallbackSaveable = (draft: FallbackDraft, ctx: FallbackContext) =>
  missingPickOf(draft) === null && fallbackIssueOf(draft, ctx) === null;

/** The stored chain as a draft, with step 3 read as `recipientOf` shows it; writes nothing. */
export function fallbackDraftOf(stored: StoredFallback): FallbackDraft {
  const draft: FallbackDraft = {
    strategy: stored.strategy,
    userId: stored.user?.id ?? '',
    orgUnitId: stored.orgUnit?.id ?? '',
    role: stored.role ?? '',
    isSelfApprovalForbidden: stored.isSelfApprovalForbidden ?? false,
    isManualApproverSelectionAllowed: stored.isManualApproverSelectionAllowed ?? false
  };
  return withRecipient(draft, recipientOf(draft), draft.userId);
}

/** What the draft would store — fields the chain does not use fold into null. */
export const fallbackInputOf = (draft: FallbackDraft) => ({
  strategy: draft.strategy,
  // The manual chain has no step 3, so it stores no user; every other chain keeps the picked one.
  userId: hasRecipientStep(draft.strategy) && draft.userId !== '' ? draft.userId : null,
  orgUnitId: usesOrgUnit(draft.strategy) && draft.orgUnitId !== '' ? draft.orgUnitId : null,
  role: usesRole(draft.strategy) && draft.role !== '' ? (draft.role as UserRole) : null,
  isSelfApprovalForbidden: draft.isSelfApprovalForbidden,
  // Stored as it stands, even under the manual chain that makes it redundant: folding it to
  // false there would quietly drop a setting on a round trip through the select.
  isManualApproverSelectionAllowed: draft.isManualApproverSelectionAllowed
});

export const isSameFallback = (left: FallbackDraft, right: FallbackDraft) => {
  const a = fallbackInputOf(left);
  const b = fallbackInputOf(right);
  return (
    a.strategy === b.strategy &&
    a.userId === b.userId &&
    a.orgUnitId === b.orgUnitId &&
    a.role === b.role &&
    a.isSelfApprovalForbidden === b.isSelfApprovalForbidden &&
    a.isManualApproverSelectionAllowed === b.isManualApproverSelectionAllowed
  );
};

/** Who gets the document when step 2 finds nobody (step 3). */
export type FallbackRecipient = 'USER' | 'OWNER_PICKS' | 'NONE';

/** Step 3 as the draft stores it; under the user chain that user is step 2, not step 3. */
export function recipientOf(draft: FallbackDraft): FallbackRecipient {
  if (!requiresUser(draft.strategy) && draft.userId !== '') return 'USER';
  return draft.isManualApproverSelectionAllowed ? 'OWNER_PICKS' : 'NONE';
}

/** The draft with step 3 set; a fallback user and the creator's pick exclude each other. */
export function withRecipient(
  draft: FallbackDraft,
  recipient: FallbackRecipient,
  userId = ''
): FallbackDraft {
  const isOwnerPick = recipient === 'OWNER_PICKS';
  if (requiresUser(draft.strategy)) {
    return { ...draft, isManualApproverSelectionAllowed: isOwnerPick };
  }
  return {
    ...draft,
    userId: recipient === 'USER' ? userId : '',
    isManualApproverSelectionAllowed: isOwnerPick
  };
}

/**
 * The draft under another chain. Step 3 carries over; the user of the user chain (step 2) and a
 * fallback user (step 3) are different picks, so neither moves into the other's place.
 */
export function withStrategy(
  draft: FallbackDraft,
  strategy: ApproverFallbackStrategy
): FallbackDraft {
  const recipient = recipientOf(draft);
  const isSameUserStep = requiresUser(draft.strategy) === requiresUser(strategy);
  return withRecipient(
    { ...draft, strategy, userId: isSameUserStep ? draft.userId : '' },
    recipient,
    draft.userId
  );
}

/** Names the chip of step 2 needs; missing ones read as "the chosen …". */
export interface ChainChipNames {
  readonly userName?: string;
  readonly orgUnitName?: string;
  readonly roleLabel?: string;
}

/** Translation key (under `approvalFlow`) and values of the step 2 chip. */
export function chainChipOf(
  draft: FallbackDraft,
  names: ChainChipNames
): { key: string; values?: Record<string, string> } {
  const { strategy } = draft;
  if (usesOrgUnit(strategy)) {
    return names.orgUnitName
      ? { key: `strategyChip.${strategy}`, values: { orgUnit: names.orgUnitName } }
      : { key: `strategyChipUnpicked.${strategy}` };
  }
  if (usesRole(strategy)) {
    return names.roleLabel
      ? { key: `strategyChip.${strategy}`, values: { role: names.roleLabel } }
      : { key: `strategyChipUnpicked.${strategy}` };
  }
  if (requiresUser(strategy)) {
    return names.userName
      ? { key: `strategyChip.${strategy}`, values: { user: names.userName } }
      : { key: `strategyChipUnpicked.${strategy}` };
  }
  return { key: `strategyChip.${strategy}` };
}

/** The lifecycle step an approved document moves on to. */
export function nextStepOf(document: LifecycleDocument): LifecycleStepRef {
  switch (document) {
    case 'invoice':
      return { document, step: 'export' };
    case 'requisition':
      return { document, step: 'ordering' };
    case 'purchaseOrder':
      return { document, step: 'closure' };
  }
}
